import { useMemo } from 'react';

import type {
  EnrichmentAttributeGroup,
  EnrichmentAttributeOption,
} from '~/components/common/enrichment-run-panel';
import { MANDATORY_ATTRIBUTE_KEYS } from '~/constants/config/enrichment-attribute-rules';
import { useScheduledEnrichmentAttributeGroups } from '~/features/content-enrichment/hooks/use-scheduled-enrichment-attribute-groups';

/**
 * Narrows the config-service taxonomy to the attributes a *specific run* actually
 * enriched, and marks every survivor selected by default — delivery scopes a
 * subset of what was produced, so it starts from "deliver everything this run
 * enriched" and lets the operator turn things off.
 *
 * `eligibleKeys` is the run's enriched set (from the persisted run config, or the
 * job-status echo once the backend returns it). When it is `undefined` the run's
 * set is unknown here — the full taxonomy is shown rather than an empty picker,
 * so the panel stays usable until that data is wired. See `docs/delivery-run-panel.md`.
 *
 * Every delivered option is independent: dependency metadata (`requiresKey`) is
 * stripped at both attribute and group level, so e.g. Phrase Descriptions stays
 * selectable with Search Descriptors off. Dependencies describe generation order
 * at creation; delivery only pushes fields the run already produced.
 */
function toDeliveryGroups(
  full: EnrichmentAttributeGroup[],
  eligibleKeys: string[] | undefined,
  alwaysOnKeys: string[] | undefined,
): EnrichmentAttributeGroup[] {
  const eligible = eligibleKeys ? new Set(eligibleKeys) : null;
  const alwaysOn = new Set(alwaysOnKeys ?? []);

  return (
    full
      .map((group) => {
        // Mandatory / always-on keys (GMC's google_product_category, Meta's
        // title/description) aren't in the run's `eligibleKeys` — they're always
        // produced, never explicitly selected — so keep them regardless so delivery
        // can push them. They are rebuilt without `mandatory` below: locked at
        // creation, but the operator may still leave them out of a delivery.
        const attributes = (
          eligible
            ? group.attributes.filter(
                (a) =>
                  eligible.has(a.key) ||
                  a.mandatory === true ||
                  MANDATORY_ATTRIBUTE_KEYS.has(a.key) ||
                  alwaysOn.has(a.key),
              )
            : group.attributes
        ).map(
          // Rebuilt without `requiresKey` / `mandatory` so no option can lock or
          // clear another — phrase descriptions no longer depend on search here.
          (a): EnrichmentAttributeOption => ({ key: a.key, label: a.label, defaultSelected: true }),
        );
        // Drop the group-level `requiresKey` too (e.g. SEARCH requires `description`).
        // That gating exists so the CREATION panel can express a generation-order
        // dependency — Search Attributes cannot be generated without Description
        // present in the same run. Delivery has no such order: it pushes fields a
        // run already enriched, so "which fields does this run have" is the only
        // question, and description being deselected here must not lock/clear an
        // unrelated group. Spreading `group` would otherwise carry `requiresKey`
        // through unchanged, since the per-attribute rebuild above never touches it.
        const { requiresKey: _groupRequiresKey, ...groupWithoutRequires } = group;
        void _groupRequiresKey;
        return { ...groupWithoutRequires, attributes };
      })
      // A group with nothing enriched is not actionable — drop it, matching how the
      // creation adapter already omits empty groups.
      .filter((group) => group.attributes.length > 0)
  );
}

/**
 * The delivery panel's attribute taxonomy: the same platform-specific source the
 * creation panel uses, narrowed to the run's enriched attributes and defaulted
 * all-on. Google and Meta behave identically — Meta delivery honours the
 * approval's `contentAttributes` and descriptor flags, so every field is offered.
 * `alwaysOnKeys` (the run's job-status echo) keeps agent-produced fields in the
 * picker even when they weren't part of the requested set.
 */
export function useDeliveryAttributeGroups(
  enabled: boolean,
  eligibleKeys: string[] | undefined,
  platform: 'google' | 'meta' = 'google',
  alwaysOnKeys?: string[],
): {
  groups: EnrichmentAttributeGroup[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  // The full taxonomy already includes every attribute (mandatory ones like
  // Google Product Category among them); delivery narrows it to the run's set below.
  const {
    groups: full,
    isLoading,
    isError,
    refetch,
  } = useScheduledEnrichmentAttributeGroups(platform, enabled);

  const groups = useMemo(
    () => toDeliveryGroups(full, eligibleKeys, alwaysOnKeys),
    [full, eligibleKeys, alwaysOnKeys],
  );

  return {
    groups,
    isLoading,
    isError,
    refetch,
  };
}
